// Libs
import classNames from 'classnames/bind';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseMoreAction } from '~/components/common';
import { SubrentalFormModal } from '~/components/module/subrental';
import { PurchaseOrderFormModal } from '~/components/module/purchaseOrder';
// Others
import { PurchaseOrderEnum } from '~/utils/enum/common';
import { IOverbookedItem } from '~/utils/interface/quotes';
import { IBaseMoreAction } from '~/utils/interface/common';
import { IOrderDetailOverbooked } from '~/utils/interface/order';
import { IPurchaseOrderItemDetail } from '~/utils/interface/purchaseOrder';
// Styles, images, icons
import styles from './TableOverbookingOrderDetail.module.scss';

type Props = {
  overbookedItems?: IOrderDetailOverbooked;
};

const cx = classNames.bind(styles);

const TableOverbookingOrderDetail = (props: Props) => {
  //#region Destructuring Props
  const { overbookedItems } = props;
  const { id: orderId } = useParams();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isOpenPurchaseOrderFormModal, setIsOpenPurchaseOrderFormModal] = useState<boolean>(false);
  const [overbookedItemSelected, setOverbookedItemSelected] = useState<IPurchaseOrderItemDetail>();
  const [isOpenSubrentalModal, setIsOpenSubrentalModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handlePurchaseOrder = (item: IOverbookedItem) => {
    handleShowPurchaseOrderFormModal();
    setOverbookedItemSelected({
      inventoryId: item?.inventoryId,
      variantId: item?.variantId,
      quantity: item?.quantity,
      rentalPrice: item?.rentalPrice,
      variantName: item?.variantName,
      name: item?.name,
    });
  };

  const handleSubrental = (item: IOverbookedItem) => {
    handleShowSubrentalModal();
    setOverbookedItemSelected({
      inventoryId: item?.inventoryId,
      variantId: item?.variantId,
      quantity: item?.overbookedQuantity,
      rentalPrice: item?.rentalPrice,
      variantName: item?.variantName,
      name: item?.name,
    });
  };

  const handleShowPurchaseOrderFormModal = () => {
    setIsOpenPurchaseOrderFormModal(true);
  };

  const handleClosePurchaseOrderFormModal = () => {
    setIsOpenPurchaseOrderFormModal(false);
    setOverbookedItemSelected(undefined);
  };

  const handleShowSubrentalModal = () => {
    setIsOpenSubrentalModal(true);
  };

  const handleCloseSubrentalModal = () => {
    setIsOpenSubrentalModal(false);
    setOverbookedItemSelected(undefined);
  };
  //#endregion Handle Function

  return (
    <Stack id='tableOverbookingOrderDetailComponent'>
      <TableContainer component={Paper} className={cx('containerTable')}>
        <Table stickyHeader>
          <TableHead className={cx('tableHead')}>
            <TableRow>
              <TableCell width={'22%'} className={cx('titleHead')}>
                {t('form_order_table_title_item')}
              </TableCell>

              <TableCell align='center' className={cx('titleHead')}>
                {t('table_overbooked_item_title_ordered')}
              </TableCell>

              <TableCell align='center' className={cx('titleHead')}>
                {t('table_overbooked_item_title_overbooked')}
              </TableCell>

              <TableCell align='center' className={cx('titleHead')}>
                {t('table_overbooked_item_title_subrented')}
              </TableCell>

              <TableCell width={80} className={cx('titleHead')}>
                {t('common_text_action', { ns: 'common/base' })}
              </TableCell>
            </TableRow>
          </TableHead>

          <Stack height={'20px'} />

          <TableBody>
            {overbookedItems?.orderItems &&
              overbookedItems?.orderItems?.length > 0 &&
              overbookedItems?.orderItems?.map((item, itemIndex) => {
                const actions: IBaseMoreAction[] = [
                  {
                    label: t('table_overbooked_item_subrental_action'),
                    onClick: () => handleSubrental(item),
                  },
                  {
                    label: t('table_overbooked_item_po_action'),
                    onClick: () => handlePurchaseOrder(item),
                  },
                ];

                return (
                  <TableRow key={itemIndex} className={cx('rowInvalid')}>
                    <TableCell width={'22%'} className={cx('textCell', 'cellInvalid')}>
                      <Stack direction={'row'} alignItems={'center'} gap={1}>
                        <>{item.name}</>
                        <>{item.variantName && ` - ${item.variantName}`}</>
                      </Stack>
                    </TableCell>

                    <TableCell align='center' className={cx('textCell', 'cellInvalid')}>
                      {item.quantity}
                    </TableCell>

                    <TableCell align='center' className={cx('textCell', 'cellInvalid')}>
                      {item.overbookedQuantity}
                    </TableCell>

                    <TableCell align='center' className={cx('textCell', 'cellInvalid')}>
                      {item.subRentalQuantity}
                    </TableCell>

                    <TableCell width={80} align='center' className={cx('textCell', 'cellInvalid')}>
                      <BaseMoreAction actions={actions} />
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {overbookedItems?.orderItems.length === 0 && (
        <Typography align='center' className={cx('noData')}>
          {t('common_no_data_available', { ns: 'common/base' })}
        </Typography>
      )}

      {isOpenPurchaseOrderFormModal && (
        <PurchaseOrderFormModal
          overbookedItemSelected={overbookedItemSelected}
          onClose={handleClosePurchaseOrderFormModal}
          type={PurchaseOrderEnum.PURCHASE}
        />
      )}

      {isOpenSubrentalModal && (
        <PurchaseOrderFormModal
          overbookedItemSelected={overbookedItemSelected}
          onClose={handleCloseSubrentalModal}
          type={PurchaseOrderEnum.SUB_RENTAL}
          orderId={orderId}
        />
        // <SubrentalFormModal overbookedItemSelected={overbookedItemSelected} onClose={handleCloseSubrentalModal} orderId={orderId} />
      )}
    </Stack>
  );
};

export default TableOverbookingOrderDetail;
